import { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { Button, StandardDialog } from '@roc/ui';
import { Box, Typography, TextareaAutosize } from '@material-ui/core';
import { ErrorOutline } from '@material-ui/icons';
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { ChatBotStore } from '../stores/chatBotStore';
import { getOrigin } from '../utils/util';

const FULLSCREEN_THANK_YOU_AUTO_CLOSE_SECONDS = 10;

enum DialogStep {
  Confirm = 'confirm',
  Feedback = 'feedback',
  ThankYou = 'thankyou',
}

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    height: '100%',
  },
  warningBox: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  alert: {
    borderRadius: '20px',
    marginBottom: theme.spacing(2),
  },
  feedbackContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  feedbackToggleGroup: {
    justifyContent: 'center',
    width: '100%',
    gap: '2px',
    '& .MuiToggleButton-root': {
      color: 'inherit',
      filter: 'grayscale(1)',
      opacity: 0.5,
    },
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: 'rgba(2, 165, 145, 0.15)',
      color: 'inherit',
      filter: 'grayscale(0)',
      opacity: 1,
      border: '2px solid #02A591 !important',
      borderRadius: '8px',
    },
  },
  faceButton: {
    border: 'none',
    fontSize: '28px',
    lineHeight: '1',
    transition: 'transform 0.15s ease',
  },
  textarea: {
    width: '100%',
    padding: theme.spacing(1.5, 2),
    borderRadius: '12px',
    border: '2px solid #e2e8f0',
    fontFamily: theme.typography.fontFamily,
    fontSize: '15px',
    lineHeight: '1.5',
    resize: 'none',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    '&:focus': {
      boxShadow: '0 0 0 3px rgba(33, 57, 94, 0.1)',
    },
    '&::placeholder': {
      color: '#94a3b8',
    },
  },
  submitFeedbackButton: {
    backgroundColor: '#02A591',
    color: theme.palette.common.white,
    '&:hover': {
      background: '#099483',
    },
    '&:disabled': {
      background: '#cbd5e1',
    },
  },
  thankYouContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  thankYouCountdownNumber: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
}));

interface NewChatConfirmationProps {
  open: boolean;
  handleCloseModal: () => void;
  handleConfirm: () => void;
}

interface SessionFeedbackDialogProps {
  open: boolean;
  isFullScreen: boolean;
  store: ChatBotStore;
  handleClose: () => void;
}

const useFullscreenThankYouAutoClose = (active: boolean, onComplete: () => void) => {
  const [secondsLeft, setSecondsLeft] = useState(FULLSCREEN_THANK_YOU_AUTO_CLOSE_SECONDS);
  const timerIdRef = useRef<number | null>(null);

  const stopTimer = useCallback(() => {
    if (timerIdRef.current != null) {
      window.clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
  }, [])

  useEffect(() => {
    if (!active) {
      stopTimer();
      return undefined;
    }
    setSecondsLeft(FULLSCREEN_THANK_YOU_AUTO_CLOSE_SECONDS);
    timerIdRef.current = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          stopTimer();
          onComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return stopTimer;
  }, [active, onComplete, stopTimer]);

  return { secondsLeft, cancelAutoClose: stopTimer };
};

export const SessionFeedbackDialog = observer(({ open, isFullScreen, store, handleClose }: SessionFeedbackDialogProps) => {
  const classes = useStyles();
  const [step, setStep] = useState<DialogStep>(DialogStep.Confirm);
  const [inputValue, setInputValue] = useState('');
  const [toggleValue, setToggleValue] = useState<number | null>(null);

  const postCloseBrowserWindow = useCallback(() => {
    window.parent.postMessage({ data: 'close-browser-window' }, getOrigin());
  }, []);

  const { secondsLeft: autoCloseSecondsLeft, cancelAutoClose } = useFullscreenThankYouAutoClose(
    open && isFullScreen && step === DialogStep.ThankYou,
    postCloseBrowserWindow
  );

  useEffect(() => {
    if (open) {
      setStep(DialogStep.Confirm);
      setInputValue('');
      setToggleValue(null);
    }
  }, [open]);

  const handleMaybeLater = () => {
    if (!isFullScreen) {
      store.closeChatBotInParent();
      handleClose();
      return;
    }
    setStep(DialogStep.ThankYou);
  };

  const handleSubmit = async () => {
    await store.sendSessionFeedback(inputValue, toggleValue);
    if (!isFullScreen) {
      store.closeChatBotInParent();
      handleClose();
      return;
    }
    setStep(DialogStep.ThankYou);
  };

  const handleDialogDismiss = () => {
    if (step === DialogStep.Confirm) {
      handleClose();
      return;
    }
    if (step === DialogStep.ThankYou && isFullScreen) {
      handleClose();
      return;
    }
    handleMaybeLater();
  };

  const getStepContent = (): { content: JSX.Element; actions: JSX.Element } => {
    switch (step) {
      case DialogStep.Confirm:
        return {
          content: (
            <Typography variant="body1">
              Are you sure you want to close the chatbot? You will lose the current conversation progress.
            </Typography>
          ),
          actions: (
            <>
              <Button
                style={{ marginRight: '8px' }}
                onClick={handleClose}
                color="primary"
                variant="outlined"
                testId="cancel-close-chatbot"
              >
                Cancel
              </Button>
              <Button
                onClick={() => setStep(DialogStep.Feedback)}
                color="primary"
                variant="contained"
                testId="confirm-close-chatbot"
              >
                Confirm
              </Button>
            </>
          ),
        };
      case DialogStep.Feedback:
        return {
          content: (
            <div className={classes.feedbackContainer}>
              <Typography variant="body1">
                Thanks for chatting! Before you go, how did I do?
              </Typography>
              <ToggleButtonGroup
                className={classes.feedbackToggleGroup}
                value={toggleValue}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) setToggleValue(newValue);
                }}
                aria-label="emoji feedback"
              >
                <ToggleButton className={classes.faceButton} value={1}>😞</ToggleButton>
                <ToggleButton className={classes.faceButton} value={2}>😐</ToggleButton>
                <ToggleButton className={classes.faceButton} value={3}>🙂</ToggleButton>
                <ToggleButton className={classes.faceButton} value={4}>😀</ToggleButton>
                <ToggleButton className={classes.faceButton} value={5}>😊</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="textSecondary">
                Any additional feedback? (optional)
              </Typography>
              <TextareaAutosize
                className={classes.textarea}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="Tell us more.."
                minRows={2}
                maxRows={4}
              />
            </div>
          ),
          actions: (
            <>
              <Button
                style={{ marginRight: '8px' }}
                onClick={handleMaybeLater}
                color="primary"
                variant="outlined"
                testId="maybe-later"
              >
                Maybe Later
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                className={classes.submitFeedbackButton}
                disabled={toggleValue === null}
                testId="submit-feedback"
              >
                Submit Feedback
              </Button>
            </>
          ),
        };
      case DialogStep.ThankYou:
        return {
          content: (
            <div className={classes.thankYouContent}>
              <Typography variant="body1">Thanks for giving your feedback!</Typography>
              {isFullScreen && (
                <Typography variant="body2" color="textSecondary" component="p" aria-live="polite">
                  This window will close automatically in{' '}
                  <span className={classes.thankYouCountdownNumber}>{autoCloseSecondsLeft}</span>
                  {' '}second{autoCloseSecondsLeft === 1 ? '' : 's'}.
                </Typography>
              )}
            </div>
          ),
          actions: (
            <>
              <Button
                style={{ marginRight: '8px' }}
                onClick={() => {
                  cancelAutoClose();
                  store.startNewChat();
                  handleClose();
                }}
                color="primary"
                variant="outlined"
                testId="start-new-chat"
              >
                Start New Chat
              </Button>
              <Button
                onClick={() => {
                  cancelAutoClose();
                  postCloseBrowserWindow();
                }}
                color="primary"
                variant="contained"
                testId="close-window"
              >
                Close Window
              </Button>
            </>
          ),
        };
    }
  };

  const { content, actions } = getStepContent();

  return (
    <StandardDialog
      open={open}
      title="Close conversation"
      maxWidth="xs"
      fullScreen={false}
      removeContentBox
      handleClose={handleDialogDismiss}
      dialogContent={content}
      dialogActions={actions}
    />
  );
});

export const NewChatConfirmation = observer(({ open, handleCloseModal, handleConfirm }: NewChatConfirmationProps) => {
  const classes = useStyles();

  const content = (
    <Box className={classes.container}>
      <Alert icon={false} severity="error" className={classes.alert}>
        <Box className={classes.warningBox}>
          <ErrorOutline />
          <Box>
            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
              Note
            </Typography>
            <Typography variant="body2">
              the message history will be cleared and it will start fresh
            </Typography>
          </Box>
        </Box>
      </Alert>
      <Typography variant="body1" gutterBottom>
        Are you sure you want to start a new chat?
      </Typography>
    </Box>
  );

  return (
    <StandardDialog
      open={open}
      title="New Chat Confirmation"
      maxWidth="xs"
      handleClose={handleCloseModal}
      dialogContent={content}
      fullScreen={false}
      dialogActions={(
        <>
          <Button
            style={{ marginRight: '8px' }}
            onClick={handleCloseModal}
            color="primary"
            variant="outlined"
            testId="cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            testId="submit"
          >
            Yes
          </Button>
        </>
      )}
    />
  );
});
